import { useEffect, useState } from 'react';

import { setAccessToken } from '../features/authSlice';
import { refreshAccessTokenFromSession } from '../services/accessTokenRefresh';
import { clearLegacyAccessTokenCookie } from '../services/token';
import { useAppDispatch, useTypedSelector } from '../store';

// One-time cleanup of localStorage keys retired by the soft-navigation auth
// fix. Users who upgraded mid-session would otherwise carry a stale
// "Session expired" message that the old code could still surface. Harmless
// on a clean install; runs once at module load.
try {
	if (typeof window !== 'undefined') {
		window.localStorage.removeItem('loginErrorMessage');
		window.localStorage.removeItem('logoutMessage');
	}
} catch {
	// ignore — private mode, storage disabled, etc.
}

export const useInitAuth = () => {
	const dispatch = useAppDispatch();
	const [isLoading, setIsLoading] = useState(true);
	const accessToken = useTypedSelector((state) => state.authState.accessToken);
	const apiUrl = useTypedSelector((state) => state.tenantConfigState.api_url);

	useEffect(() => {
		let cancelled = false;

		const initAuth = async () => {
			setIsLoading(true);
			if (accessToken) {
				setIsLoading(false);
				return;
			}

			try {
				const refreshedToken = await refreshAccessTokenFromSession({
					apiUrl: apiUrl || process.env.REACT_APP_API_URL,
				});
				if (refreshedToken) {
					clearLegacyAccessTokenCookie();
					if (!cancelled) {
						dispatch(setAccessToken(refreshedToken));
					}
				}
			} finally {
				if (!cancelled) {
					setIsLoading(false);
				}
			}
		};

		void initAuth();
		return () => {
			cancelled = true;
		};
	}, [accessToken, apiUrl, dispatch]);

	return { isLoading };
};
